import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import { formatINR } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClipboardList, Plus, Trash2, Truck, AlertTriangle, CheckCircle2, Pencil, FileUp, Loader2 } from "lucide-react";

const STAGES = ["Confirmed", "In Production", "Dispatched", "Delivered"];
const inputCls =
  "w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500";

const isDelayed = (o) =>
  o.expected_delivery && o.status !== "Delivered" && new Date(o.expected_delivery) < new Date();

const isDueSoon = (o) => {
  if (!o.expected_delivery || o.status === "Delivered") return false;
  if (o.updated_at || o.acknowledged) return false;
  const diff = (new Date(o.expected_delivery) - new Date()) / 86400000;
  return diff >= 0 && diff <= 3;
};

export default function Orders({ onChanged }) {
  const { isOwner } = useAuth();
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState({ company_name: "", product: "", quantity: 1, order_value: 0, expected_delivery: "", latest_update: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/orders`);
      setOrders(res.data);
    } catch {
      /* ignore */
    }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.product) {
      toast.error("Company and product are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/orders`, { ...form, quantity: Number(form.quantity) || 1, order_value: Number(form.order_value) || 0 });
      toast.success("Order added");
      setForm({ company_name: "", product: "", quantity: 1, order_value: 0, expected_delivery: "", latest_update: "", notes: "" });
      await load();
      onChanged && onChanged();
    } catch {
      toast.error("Failed to add order");
    } finally {
      setSaving(false);
    }
  };

  const patch = async (id, upd) => {
    try {
      await axios.patch(`${API}/orders/${id}`, upd);
      await load();
      onChanged && onChanged();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/orders/${id}`);
      await load();
      onChanged && onChanged();
    } catch {
      toast.error("Delete failed");
    }
  };

  const openEdit = (o) => {
    setEditing(o);
    setEditForm({
      company_name: o.company_name,
      product: o.product,
      quantity: o.quantity,
      order_value: o.order_value,
      status: o.status,
      expected_delivery: o.expected_delivery || "",
      latest_update: o.latest_update || "",
      notes: o.notes || "",
    });
  };

  const saveEdit = async () => {
    if (!editForm.company_name || !editForm.product) {
      toast.error("Company and product are required");
      return;
    }
    try {
      await axios.patch(`${API}/orders/${editing.id}`, {
        ...editForm,
        quantity: Number(editForm.quantity) || 1,
        order_value: Number(editForm.order_value) || 0,
      });
      toast.success("Order updated");
      setEditing(null);
      setEditForm(null);
      await load();
      onChanged && onChanged();
    } catch {
      toast.error("Update failed");
    }
  };

  const uploadInvoice = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/proforma/extract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data || {};
      await axios.post(`${API}/orders`, {
        company_name: d.company_name || "Unknown company",
        product: d.product || "",
        quantity: Number(d.quantity) || 1,
        order_value: Number(d.sales_value) || 0,
      });
      toast.success("Order created from document", {
        description: `${d.company_name || ""} · ${d.product || ""}`,
      });
      await load();
      onChanged && onChanged();
    } catch {
      toast.error("Could not read the document");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-5xl w-full mx-auto" data-testid="orders-page">
      <header className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.14em] text-blue-600 font-semibold">Fulfilment</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1">Current orders</h1>
        <p className="text-sm text-slate-500 mt-1">Track order status, delivery, latest update and any delays.</p>
      </header>

      <label
        data-testid="order-upload-label"
        className="mb-5 flex items-center gap-2.5 px-4 h-12 rounded-lg border border-dashed border-blue-300 bg-blue-50 text-sm font-medium text-blue-700 cursor-pointer hover:bg-blue-100"
        style={{ transition: "background-color 150ms ease" }}
      >
        {uploading ? <Loader2 size={16} className="animate-spin" /> : <FileUp size={16} />}
        {uploading ? "Reading document…" : "Upload an invoice (image or PDF) to auto-create an order"}
        <input
          data-testid="order-upload-input"
          type="file"
          accept="image/*,application/pdf,.pdf"
          className="hidden"
          onChange={(e) => uploadInvoice(e.target.files[0] || null)}
        />
      </label>

      <form onSubmit={add} data-testid="order-add-form" className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <input data-testid="order-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Company *" className={inputCls} />
        <input data-testid="order-product" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="Product *" className={inputCls} />
        <input data-testid="order-qty" type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="Qty" className={inputCls} />
        <input data-testid="order-value" type="number" min="0" value={form.order_value} onChange={(e) => setForm({ ...form, order_value: e.target.value })} placeholder="Order value ₹" className={inputCls} />
        <div>
          <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Expected delivery</label>
          <input data-testid="order-eta" type="date" value={form.expected_delivery} onChange={(e) => setForm({ ...form, expected_delivery: e.target.value })} className={inputCls} />
        </div>
        <input data-testid="order-update" value={form.latest_update} onChange={(e) => setForm({ ...form, latest_update: e.target.value })} placeholder="Latest update" className={`${inputCls} lg:col-span-2 sm:mt-[18px]`} />
        <button data-testid="order-add-btn" type="submit" disabled={saving} className="inline-flex items-center justify-center gap-1.5 h-10 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-md sm:mt-[18px]" style={{ transition: "background-color 160ms ease" }}>
          <Plus size={16} /> Add order
        </button>
      </form>

      <div className="space-y-4">
        {orders.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm py-12 text-center text-sm text-slate-500">
            No orders yet. Add one above to start tracking fulfilment.
          </div>
        )}
        {orders.map((o, idx) => {
          const stageIdx = STAGES.indexOf(o.status);
          const delayed = isDelayed(o);
          return (
            <div key={o.id} data-testid={`order-card-${idx}`} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-lg font-bold text-slate-900">{o.company_name}</span>
                    {o.status === "Delivered" ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"><CheckCircle2 size={11} /> Delivered</span>
                    ) : delayed ? (
                      <span data-testid={`order-delayed-${idx}`} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700"><AlertTriangle size={11} /> Delayed</span>
                    ) : isDueSoon(o) ? (
                      <span data-testid={`order-duesoon-${idx}`} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><AlertTriangle size={11} /> Due soon</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700"><Truck size={11} /> On time</span>
                    )}
                  </div>
                  <div className="text-sm text-slate-500 mt-0.5">{o.product} · Qty {o.quantity} · {formatINR(o.order_value)}</div>
                </div>
                <div className="flex items-center gap-2">
                  {isDueSoon(o) && (
                    <button
                      data-testid={`order-ack-${idx}`}
                      onClick={() => patch(o.id, { acknowledged: true })}
                      className="h-9 px-2.5 rounded-md border border-amber-300 bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100"
                      style={{ transition: "background-color 160ms ease" }}
                    >
                      Mark on track
                    </button>
                  )}
                  <select data-testid={`order-status-${idx}`} value={o.status} onChange={(e) => patch(o.id, { status: e.target.value })} className="h-9 px-2 rounded-md border border-slate-200 bg-white text-sm">
                    {STAGES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <button data-testid={`order-edit-${idx}`} onClick={() => openEdit(o)} title="Edit order" className="text-slate-400 hover:text-blue-600 p-2" style={{ transition: "color 160ms ease" }}><Pencil size={16} /></button>
                  {isOwner && (
                    <button data-testid={`order-delete-${idx}`} onClick={() => remove(o.id)} className="text-slate-400 hover:text-rose-600 p-2"><Trash2 size={16} /></button>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 mt-4">
                {STAGES.map((s, i) => (
                  <div key={s} className="flex-1">
                    <div className={`h-1.5 rounded-full ${i <= stageIdx ? "bg-blue-600" : "bg-slate-200"}`} style={{ transition: "background-color 200ms ease" }} />
                    <div className={`text-[10px] mt-1 ${i <= stageIdx ? "text-blue-700 font-semibold" : "text-slate-400"}`}>{s}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100 text-sm">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Expected delivery</div>
                  <input data-testid={`order-eta-edit-${idx}`} type="date" defaultValue={o.expected_delivery || ""} onBlur={(e) => e.target.value !== (o.expected_delivery || "") && patch(o.id, { expected_delivery: e.target.value })} className="mt-1 h-9 px-2 rounded-md border border-slate-200 text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Latest update</div>
                  <input data-testid={`order-update-edit-${idx}`} defaultValue={o.latest_update || ""} onBlur={(e) => e.target.value !== (o.latest_update || "") && patch(o.id, { latest_update: e.target.value })} placeholder="e.g. Dispatched via BlueDart, AWB 12345" className="mt-1 w-full h-9 px-2 rounded-md border border-slate-200 text-sm" />
                </div>
              </div>
              <div className="mt-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Notes</div>
                <textarea data-testid={`order-notes-edit-${idx}`} defaultValue={o.notes || ""} onBlur={(e) => e.target.value !== (o.notes || "") && patch(o.id, { notes: e.target.value })} placeholder="Any issue, delay reason, or special instruction…" className="mt-1 w-full min-h-[60px] px-2.5 py-2 rounded-md border border-slate-200 text-sm" />
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setEditForm(null); } }}>
        <DialogContent data-testid="order-edit-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit order</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Company</label>
                <input data-testid="edit-order-company" value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} className={`${inputCls} mt-1`} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Product(s)</label>
                <input data-testid="edit-order-product" value={editForm.product} onChange={(e) => setEditForm({ ...editForm, product: e.target.value })} className={`${inputCls} mt-1`} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Quantity</label>
                <input data-testid="edit-order-qty" type="number" min="1" value={editForm.quantity} onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })} className={`${inputCls} mt-1`} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Order value ₹</label>
                <input data-testid="edit-order-value" type="number" min="0" value={editForm.order_value} onChange={(e) => setEditForm({ ...editForm, order_value: e.target.value })} className={`${inputCls} mt-1`} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Expected delivery</label>
                <input data-testid="edit-order-eta" type="date" value={editForm.expected_delivery} onChange={(e) => setEditForm({ ...editForm, expected_delivery: e.target.value })} className={`${inputCls} mt-1`} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Status</label>
                <select data-testid="edit-order-status" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className={`${inputCls} mt-1`}>
                  {STAGES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Latest update</label>
                <input data-testid="edit-order-update" value={editForm.latest_update} onChange={(e) => setEditForm({ ...editForm, latest_update: e.target.value })} className={`${inputCls} mt-1`} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Notes</label>
                <textarea data-testid="edit-order-notes" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="mt-1 w-full min-h-[70px] px-2.5 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => { setEditing(null); setEditForm(null); }} className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
            <button data-testid="edit-order-save" onClick={saveEdit} className="h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">Save changes</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
